import { useState } from 'react';

interface AvatarFeedbackProps {
  selectedAvatarImageUrl: string;
  onComplete: (feedback: AvatarFeedbackData) => void | Promise<void>;
  isSubmitting?: boolean;
}

export interface AvatarFeedbackData {
  looksLikeMe: number; // 1-4 scale
  isInspiring: number; // 1-4 scale
}

const feedbackQuestions = [
  {
    id: 'looksLikeMe',
    label: 'The avatar I selected looks like me',
    options: ['Strongly disagree', 'Disagree', 'Agree', 'Strongly agree'],
  },
  {
    id: 'isInspiring',
    label: 'The avatar I selected is inspiring to me',
    options: ['Strongly disagree', 'Disagree', 'Agree', 'Strongly agree'],
  },
];

export function AvatarFeedback({ selectedAvatarImageUrl, onComplete, isSubmitting = false }: AvatarFeedbackProps) {
  const [feedback, setFeedback] = useState<Record<string, number>>({});

  const handleAnswer = (questionId: string, value: number) => {
    setFeedback(prev => ({ ...prev, [questionId]: value }));
  };

  const allAnswered = feedbackQuestions.every(q => feedback[q.id] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered) return;

    await onComplete({
      looksLikeMe: feedback.looksLikeMe || 1,
      isInspiring: feedback.isInspiring || 1,
    });
  };

  return (
    <div className="tw-flex-1 tw-grid tw-grid-cols-[2fr_1fr] tw-min-h-0">
      <div className="tw-p-4 tw-overflow-y-auto tw-bg-white">
        <div className="tw-space-y-6">
          <div className="tw-space-y-1">
            <h2 className="tw-text-xl tw-font-bold tw-text-gray-800">How do you feel about your avatar?</h2>
            <p className="tw-text-sm tw-text-gray-600">Please share your thoughts about the avatar you selected.</p>
          </div>

          <div className="tw-space-y-6">
            {feedbackQuestions.map((question, qIndex) => (
              <div key={question.id} className="tw-space-y-2">
                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">
                  {qIndex + 1}. {question.label}
                </h3>
                <div className="tw-grid tw-grid-cols-4 tw-gap-2">
                  {question.options.map((option, optionIndex) => {
                    const value = optionIndex + 1;
                    const isSelected = feedback[question.id] === value;

                    return (
                      <button
                        key={optionIndex}
                        onClick={() => handleAnswer(question.id, value)}
                        disabled={isSubmitting}
                        className={`tw-p-2 tw-text-center tw-rounded-lg tw-transition-all tw-border-2 tw-min-h-[3rem] tw-flex tw-items-center tw-justify-center tw-text-xs ${
                          isSelected
                            ? 'tw-border-blue-600 tw-bg-blue-50 tw-font-semibold'
                            : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-300'
                        } ${isSubmitting ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="tw-pt-4">
            <button
              onClick={handleSubmit}
              disabled={!allAnswered || isSubmitting}
              className={`tw-w-full tw-py-2 tw-px-4 tw-rounded-lg tw-font-semibold tw-transition-all ${
                allAnswered && !isSubmitting
                  ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700'
                  : 'tw-bg-gray-200 tw-text-gray-500 tw-cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Completing...' : 'Complete'}
            </button>
          </div>
        </div>
      </div>

      <div className="tw-p-4 tw-bg-gray-50 tw-border-l tw-border-gray-200 tw-flex tw-flex-col tw-items-center tw-justify-start">
        <div className="tw-max-w-md tw-w-full tw-space-y-2">
          <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-text-center">Your Avatar</h3>
          <img
            src={selectedAvatarImageUrl}
            alt="Your selected avatar"
            className="tw-w-full tw-h-auto tw-rounded-xl tw-border-4 tw-border-blue-600"
          />
        </div>
      </div>
    </div>
  );
}
